import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/cn';

const dialogRootId = 'confirm-dialog-root';

function ensureDialogRoot() {
  let root = document.getElementById(dialogRootId);
  if (!root) {
    root = document.createElement('div');
    root.id = dialogRootId;
    document.body.appendChild(root);
  }
  return root;
}

export default function ConfirmDialog({
  open,
  title = '¿Estás seguro?',
  description = 'Esta acción no se puede deshacer.',
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  intent = 'danger',
  onConfirm,
  onCancel,
  destructive = true,
}) {
  useEffect(() => {
    if (!open) return;
    const handler = (event) => {
      if (event.key === 'Escape') {
        onCancel?.();
      }
    };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handler);
    };
  }, [open, onCancel]);

  if (!open) return null;

  const node = ensureDialogRoot();

  const intentClasses = destructive
    ? 'bg-red-600 hover:bg-red-700 focus-visible:outline-red-400'
    : 'bg-blue-600 hover:bg-blue-700 focus-visible:outline-blue-400';

  const iconClasses = cn(
    'flex h-12 w-12 items-center justify-center rounded-full',
    {
      'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400': destructive && intent !== 'warning',
      'bg-yellow-50 text-yellow-600 dark:bg-yellow-500/10 dark:text-yellow-300': intent === 'warning',
      'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-300': !destructive && intent !== 'warning',
    }
  );

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/70 px-4 py-6">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        className="w-full max-w-md rounded-2xl bg-white ring-1 ring-black/5 focus:outline-none dark:bg-gray-900 dark:ring-white/10"
      >
        <div className="flex items-start gap-4 border-b border-gray-100 px-6 py-5 dark:border-gray-800">
          <div className={iconClasses}>
            <AlertTriangle className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h2 id="confirm-dialog-title" className="text-lg font-semibold text-gray-900 dark:text-gray-50">
              {title}
            </h2>
            <p id="confirm-dialog-description" className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {description}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2 px-6 py-5 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center justify-center rounded-full border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={cn('inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold text-white transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2', intentClasses)}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    node
  );
}
